import { SubmittableTechnology, Technology } from '../../backend/models/technology';
import { Ring } from '../../backend/models/ring';
import { Section } from '../../backend/models/section';
import { Tag } from '../../backend/models/tag';
import { Client, SubmittableClient } from '../../backend/models/client';
import { Person } from '../../backend/models/person';

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: process.env.REACT_APP_API_KEY ?? '',
};

export async function addTechnology(technology: SubmittableTechnology): Promise<Technology> {
  const result = await fetch('/admin/addTechnology', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(technology),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const newTechnology = await result.json();
  return newTechnology;
}

export function deleteTechnology(id: string) {
  return fetch('/admin/deleteTechnology', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify({ id }),
  });
}

export async function updateTechnology(technology: SubmittableTechnology, id?: string): Promise<Technology> {
  technology.id = id;
  const result = await fetch('/admin/updateTechnology', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(technology, (k, v) => (v === undefined ? null : v)),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const updatedTechnology = await result.json();
  return updatedTechnology;
}

export async function getTechnologies(): Promise<Technology[]> {
  const result = await fetch(`/api/technologies`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const technologies = (await result.json()) as Technology[];
  return technologies.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export async function getTechnologiesInSection(section: string): Promise<Technology[]> {
  const result = await fetch(`/api/technologies/section/${section}`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const technologies = await result.json();
  return technologies;
}

export async function getTechnologiesInRing(ring: string): Promise<Technology[]> {
  const result = await fetch(`/api/technologies/ring/${ring}`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const technologies = await result.json();
  return technologies;
}

export async function getTechnology(name: string | undefined): Promise<Technology> {
  let result;
  if (name) {
    result = await fetch(`/api/technology/${name}`);
  } else {
    throw new Error('Specify technology name');
  }

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const technology = await result.json();
  return technology;
}

export async function uploadLogo(logo: File) {
  const data = new FormData();
  data.append('file', logo);

  const result = await fetch('/admin/uploadLogo', {
    method: 'POST',
    headers: {
      Authorization: process.env.REACT_APP_API_KEY ?? '',
    },
    body: data,
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const url = await result.json();
  return url;
}

export async function updateRings(created: Ring[], deleted: Ring[], updated: Ring[]) {
  const body = { created, deleted, updated };
  return fetch('/admin/updateRings', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(body),
  });
}

export async function updateRing(ring: Ring) {
  const body = { ring };
  return fetch('/admin/updateRing', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(body),
  });
}

export async function getRings(): Promise<Ring[]> {
  const result = await fetch(`/admin/rings`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const rings = (await result.json()) as Ring[];
  return rings.sort((a, b) => a.order - b.order);
}

export async function updateSections(created: Section[], deleted: Section[], updated: Section[]) {
  const body = { created, deleted, updated };
  return fetch('/admin/updateSections', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(body),
  });
}

export async function updateSection(section: Section) {
  const body = { section };
  return fetch('/admin/updateSection', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(body),
  });
}

export async function getSections(): Promise<Section[]> {
  const result = await fetch(`/admin/sections`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const sections = (await result.json()) as Section[];
  return sections.sort((a, b) => a.order - b.order);
}

export async function getTags(): Promise<Tag[]> {
  const result = await fetch(`/admin/tags`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const tags = (await result.json()) as Tag[];
  return tags.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export async function addTag(tag: string): Promise<Tag> {
  const result = await fetch('/admin/addTag', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify({ name: tag }),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const newTag = await result.json();
  return newTag;
}

export async function getClients(): Promise<Client[]> {
  const result = await fetch(`/admin/clients`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const clients = (await result.json()) as Client[];
  return clients.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export async function getClient(name: string | undefined): Promise<Client> {
  let result;
  if (name) {
    result = await fetch(`/admin/client/${name}`);
  } else {
    throw new Error('Specify client name');
  }

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const client = await result.json();
  return client;
}

export async function addClient(client: SubmittableClient): Promise<Client> {
  const result = await fetch('/admin/addClient', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(client),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const newClient = await result.json();
  return newClient;
}

export async function updateClient(client: SubmittableClient, id: string): Promise<Client> {
  client.id = id;
  const result = await fetch('/admin/updateClient', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify(client, (k, v) => (v === undefined ? null : v)),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const updatedClient = await result.json();
  return updatedClient;
}

export function deleteClient(id: string) {
  return fetch('/admin/deleteClient', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify({ id }),
  });
}

export async function getPersons(): Promise<Person[]> {
  const result = await fetch(`/admin/persons`);

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const persons = (await result.json()) as Person[];
  return persons.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export async function addPerson(person: string): Promise<Person> {
  const result = await fetch('/admin/addPerson', {
    method: 'post',
    headers: defaultHeaders,
    body: JSON.stringify({ name: person }),
  });

  if (!result.ok) {
    throw new Error(result.statusText);
  }

  const newPerson = await result.json();
  return newPerson;
}
